const SCROLL_LOCK_CLASS = 'sds-scroll-locked';

/**
 * Takes the document out of the scroll chain while the sheet is open.
 *
 * `overflow: hidden` on the body alone is not enough: a drag that starts on the sheet's
 * header, promo or buttons finds no scrollable ancestor inside the panel and chains
 * through to the page, which is how the homepage crept along behind it. Pinning the body
 * removes the document scroller outright, and the stored offset keeps the page still.
 */
function lockPageScroll() {
  const root = document.documentElement;
  if (root.classList.contains(SCROLL_LOCK_CLASS)) return;

  const offset = window.scrollY || window.pageYOffset || 0;
  root.dataset.scrollLockOffset = String(offset);
  root.classList.add(SCROLL_LOCK_CLASS);
  document.body.style.top = `-${offset}px`;
}

function unlockPageScroll() {
  const root = document.documentElement;
  if (!root.classList.contains(SCROLL_LOCK_CLASS)) return;

  const offset = Number(root.dataset.scrollLockOffset) || 0;
  root.classList.remove(SCROLL_LOCK_CLASS);
  delete root.dataset.scrollLockOffset;
  document.body.style.top = '';
  window.scrollTo(0, offset);
}

/** True when `el` still has room to move in the direction of `deltaY`. */
function canScrollY(el, deltaY) {
  if (!el) return false;
  const max = el.scrollHeight - el.clientHeight;
  if (max <= 1) return false;
  if (deltaY < 0) return el.scrollTop > 0;
  if (deltaY > 0) return el.scrollTop < max - 1;
  return true;
}

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.backdrop = this.querySelector('.sds-cart-notify__backdrop');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
    this.onWheel = this.handleWheel.bind(this);
    this.touchStartY = 0;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Only close controls — never the promo apply button inside the notification.
    this.querySelectorAll(
      '.cart-notification__close, .cart-notification__links > button[type="button"]'
    ).forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.close.bind(this));
    }
  }

  open() {
    this.classList.add('is-open');
    document.dispatchEvent(new CustomEvent('cart:notification-open'));

    if (this.backdrop) {
      this.backdrop.hidden = false;
      this.backdrop.setAttribute('aria-hidden', 'false');
    }

    this.notification.classList.add('animate', 'active');
    lockPageScroll();
    this.bindScrollGuards();

    this.notification.addEventListener('transitionend', () => {
      this.notification.focus();
      trapFocus(this.notification);
    }, { once: true });

    window.setTimeout(() => {
      if (this.classList.contains('is-open') && document.activeElement !== this.notification) {
        this.notification.focus();
        trapFocus(this.notification);
      }
    }, 350);

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    /* Header must freeze before unlock: removing the lock class drops
       scrollY to 0, then scrollTo restores it. That pair looks like a
       swipe and moves the grid under the sheet. */
    document.dispatchEvent(new CustomEvent('cart:notification-close'));
    this.notification.classList.remove('active');
    this.classList.remove('is-open');

    if (this.backdrop) {
      this.backdrop.hidden = true;
      this.backdrop.setAttribute('aria-hidden', 'true');
    }

    this.unbindScrollGuards();
    unlockPageScroll();
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  /**
   * CSS `overscroll-behavior` only holds while the product list is actually overflowing.
   * A five-step routine that fits the sheet leaves nowhere for the gesture to land, so the
   * homepage behind used to take the drag. These listeners keep every gesture that cannot
   * move the list from reaching the document.
   */
  bindScrollGuards() {
    if (this.scrollGuardsBound) return;
    this.scrollGuardsBound = true;
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    // Must be non-passive so preventDefault can stop the page from moving.
    this.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.addEventListener('wheel', this.onWheel, { passive: false });
  }

  unbindScrollGuards() {
    if (!this.scrollGuardsBound) return;
    this.scrollGuardsBound = false;
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchmove', this.onTouchMove);
    this.removeEventListener('wheel', this.onWheel);
  }

  productList() {
    return this.querySelector('.cart-notification-product');
  }

  handleTouchStart(event) {
    if (!event.touches || !event.touches.length) return;
    this.touchStartY = event.touches[0].clientY;
  }

  handleTouchMove(event) {
    if (!this.classList.contains('is-open') || !event.touches || !event.touches.length) return;

    const list = this.productList();
    const deltaY = this.touchStartY - event.touches[0].clientY;
    const onList = list && list.contains(event.target);

    if (onList && canScrollY(list, deltaY)) return;
    event.preventDefault();
  }

  handleWheel(event) {
    if (!this.classList.contains('is-open')) return;

    const list = this.productList();
    const onList = list && list.contains(event.target);
    if (onList && canScrollY(list, event.deltaY)) return;
    event.preventDefault();
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.dataset.cartItemKey = parsedState.key || '';
    this.dataset.variantId = String(parsedState.variant_id || parsedState.id || '');
    // A single-product add replaces whatever a previous multi-item add left behind.
    this.dataset.addedItemKeys = parsedState.key || '';
    this.getSectionsToRender().forEach((section) => {
      const host = document.getElementById(section.id);
      if (!host) return;
      const html = section.keepWrapper
        ? this.getSectionOuterHTML(parsedState.sections[section.id], section.selector)
        : this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html !== null) host.innerHTML = html;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
        // Keeping the wrapper preserves the item id and quantity that the promo box
        // reads, and matches what the routine builder injects.
        keepWrapper: true,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  parseSection(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = this.parseSection(html, selector);
    return node ? node.innerHTML : null;
  }

  getSectionOuterHTML(html, selector = '.shopify-section') {
    const node = this.parseSection(html, selector);
    return node ? node.outerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
